import {
  BOARD_SIZE,
  boardFromValues,
  candidateMask,
  conflictingCells,
  type Board,
  type CellValue,
} from './board';

type SearchState = {
  count: number;
  firstSolution: Board | null;
};

function search(values: CellValue[], limit: number, state: SearchState): void {
  if (state.count >= limit) {
    return;
  }

  let bestIndex = -1;
  let bestMask = 0;
  let bestCount = BOARD_SIZE;
  const board = boardFromValues(values);

  for (let index = 0; index < BOARD_SIZE; index += 1) {
    if (values[index] !== 0) {
      continue;
    }
    const mask = candidateMask(board, index);
    const count = countBits(mask);
    if (count === 0) {
      return;
    }
    if (count < bestCount) {
      bestIndex = index;
      bestMask = mask;
      bestCount = count;
      if (count === 1) {
        break;
      }
    }
  }

  if (bestIndex === -1) {
    state.count += 1;
    state.firstSolution ??= board;
    return;
  }

  for (let value = 1; value <= 9 && state.count < limit; value += 1) {
    if ((bestMask & (1 << value)) === 0) {
      continue;
    }
    values[bestIndex] = value as CellValue;
    search(values, limit, state);
  }
  values[bestIndex] = 0;
}

function countBits(mask: number): number {
  let remaining = mask;
  let count = 0;
  while (remaining !== 0) {
    remaining &= remaining - 1;
    count += 1;
  }
  return count;
}

function searchBoard(board: Board, limit: number): SearchState {
  const state: SearchState = { count: 0, firstSolution: null };
  if (conflictingCells(board).size > 0) {
    return state;
  }
  search([...board], Math.max(1, limit), state);
  return state;
}

export function solve(board: Board): Board | null {
  return searchBoard(board, 1).firstSolution;
}

export function countSolutions(board: Board, limit = 2): number {
  return searchBoard(board, limit).count;
}
